import { useNavigate } from 'react-router-dom'

import type { TagShopProduct } from 'types'

import { Box, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { ShoppingCart, CircleXIcon, RefreshCwIcon } from 'lucide-react'

import { Button, Image } from '@repo/ui'

import { ActionDropdown } from 'components'
import { ROUTES } from 'routes'

interface DefaultTagProductInfoProps {
  tagProduct: TagShopProduct
  onRemoveDefault?: () => void
  onChangeDefault?: () => void
}

export const DefaultTagProductInfo = ({ tagProduct, onRemoveDefault, onChangeDefault }: DefaultTagProductInfoProps) => {
  const theme = useTheme()
  const navigate = useNavigate()

  // Get quantity from first variant
  const firstVariant = tagProduct.variants?.[0]
  const quantity = firstVariant?.quantity || 0

  // Get the first image or check if images exist
  const hasImages = tagProduct.images && tagProduct.images.length > 0
  const imageSrc = hasImages ? tagProduct.images[0] : ''

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        // NO padding, surface, border or radius of its own.
        //
        // This renders INSIDE the product page's titled "Default tag" `InfoBlock`,
        // which supplies all four. It used to be a standalone card (`p: 3.5` +
        // background.paper + a `primary[100]` border + `SURFACE_RADIUS`), and keeping
        // any of that here would draw a second card inside the first — a nested
        // border 24px in from the one around it, which is the one thing the card
        // treatment was asked for in order to remove.
        //
        // The row's own geometry (image 60px, flex text, right-hand actions) is
        // unchanged; only the chrome around it is gone.
      }}
    >
      <Stack direction="row" alignItems="center" spacing={3} sx={{ flex: 1, minWidth: 0 }}>
        {/* Tag Image or Placeholder */}
        <Box sx={{ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: 2 }}>
          {hasImages ? (
            <Image
              src={imageSrc}
              alt={tagProduct.title || ''}
              fit="cover"
              sx={{
                borderRadius: 2,
                width: 60,
                height: 60
              }}
            />
          ) : (
            <Box
              sx={{
                borderRadius: 2,
                width: 60,
                height: 60,
                backgroundColor: theme.palette.grey[100],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px', textAlign: 'center' }}>
                No Image
              </Typography>
            </Box>
          )}
        </Box>

        {/* Product Information - Name and Pieces in same row */}
        <Stack direction="row" alignItems="center" spacing={3} sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            noWrap
            variant="body2"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.primary,
              minWidth: 0,
            }}
          >
            {tagProduct.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.875rem',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            {quantity} pieces
          </Typography>
        </Stack>
      </Stack>

      {/* Right side actions — flexShrink:0 so the group is never squeezed (which made
          'Buy now' wrap to two lines on a zoomed-out / narrow layout). */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0, pl: 2 }}>
        <Button
          variant="secondary"
          startIcon={<ShoppingCart size={16} />}
          onClick={() => navigate(ROUTES.TAGS_SHOP_PRODUCT(tagProduct.id))}
          sx={{
            minWidth: 'auto',
            px: 2,
            py: 1,
            fontSize: '0.875rem',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            borderColor: theme.palette.grey[300],
            color: theme.palette.text.primary,
            '&:hover': {
              borderColor: theme.palette.grey[400],
              backgroundColor: theme.palette.grey[50],
            }
          }}
        >
          Buy now
        </Button>

        {/* Fixed-width wrapper: the shared ActionDropdown's kebab IconButton is
            width:100%, so it needs a bounded parent to stay contained within the card. */}
        <Box sx={{ width: 40, flexShrink: 0 }}>
          <ActionDropdown
            items={[
              {
                label: 'Change',
                icon: <RefreshCwIcon strokeWidth={1} size={20} />,
                onClick: () => onChangeDefault?.(),
              },
              {
                label: 'Remove',
                icon: <CircleXIcon strokeWidth={1} size={20} />,
                destructive: true,
                onClick: () => onRemoveDefault?.(),
              },
            ]}
          />
        </Box>
      </Stack>
    </Box>
  )
}
